import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faBoxOpen,
  faCaretDown,
  faCaretUp,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useParams } from "react-router-dom";
import Switch from "./Switch";
import CustomDropdown from "./CustomDropdown";
import Dropdown from "./Dropdown";
import { format } from "date-fns";

const TableView = ({
  data = [],
  selectedRows = [],
  setSelectedRows,
  columns = [],
  primaryKey = "id",
  basePath = "/",
  handleDelete,
  showActions = true,
  noDelete = false,
  noEdit = false,
  noCheckbox = false,
  disableLink = false,
  radioColumnKey = "isPrimary",
  onRadioSelect,
  radioGroupName, 
  disableSort = false,
  isRowDisabled = () => false,
  defaultRowsPerPage = 100,
}) => {
  const params = useParams();
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [page, setPage] = useState(1);

  const handleSort = (column) => {
    setSortOrder((prev) =>
      sortColumn === column && prev === "asc" ? "desc" : "asc"
    );
    setSortColumn(column);
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? faCaretDown : faCaretUp;
    }
    return null;
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = (a[sortColumn] || "").toString().toLowerCase();
    const bValue = (b[sortColumn] || "").toString().toLowerCase();
    return sortOrder === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  const paginatedData = sortedData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleSelectAll = (checked) => {
    setSelectedRows(
      checked ? paginatedData.map((item) => item[primaryKey]) : []
    );
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const numberOfPages = Math.ceil(sortedData.length / rowsPerPage);
  const startRow = (page - 1) * rowsPerPage + 1;
  const endRow = Math.min(page * rowsPerPage, sortedData.length);

  return (
    <div className="overflow-x-auto pb-[250px] mb-[-250px]">
      <table className="table-default w-full">
        <thead>
          <tr>
            {!noCheckbox && (
              <th>
                <Switch
                  type="checkbox"
                  checked={
                    paginatedData.length > 0 &&
                    selectedRows.length === paginatedData.length
                  }
                  onChange={handleSelectAll}
                  disabled={paginatedData.length === 0}
                />
              </th>
            )}
            {columns.map(({ key, label}) => (
              <th
                key={key}
                className="text-left cursor-pointer"
                onClick={() => !disableSort && handleSort(key)}
              >
                {label}
                {getSortIcon(key) && (
                  <FontAwesomeIcon
                    icon={getSortIcon(key)}
                    className="w-4 pl-2"
                  />
                )}
              </th>
            ))}
            {showActions && <th></th>}
          </tr>
        </thead>
        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((item, index) => {
              const disabled = isRowDisabled(item);
              return (
              <tr key={index}
              className={`${disabled ? "cursor-not-allowed" : ""}`}>
                {!noCheckbox && (
                  <td>
                    <Switch
                      type="checkbox"
                      checked={selectedRows.includes(item[primaryKey])}
                      onChange={() => handleSelectRow(item[primaryKey])}
                    />
                  </td>
                )}
                {columns.map(({ key, type, render  }) => (
                  <td key={key}>
                    {render ? render(item) : key === radioColumnKey ? (                    
                      <input
                        type="radio"
                        name={radioGroupName}
                        checked={item[key] === true}
                        onChange={() => onRadioSelect(item[primaryKey])}
                      />
                    ) : disableLink || isRowDisabled(item) ? (
                      item[key] ? (
                        type === "date" || key.includes("Date") ? (
                          format(new Date(item[key]), "yyyy-MM-dd")
                        ) : (
                          item[key]
                        )
                      ) : (
                        "N/A"
                      )
                    ) : (
                      <Link
                        to={
                          basePath.replace(
                            ":agreementPublicId",
                            params.agreementPublicId
                          ) + `/${item[primaryKey]}`
                        }
                        className="cursor-pointer"
                      >
                        {item[key]
                          ? type === "date" || key.includes("Date")
                            ? format(new Date(item[key]), "yyyy-MM-dd")
                            : item[key]
                          : "N/A"}
                      </Link>
                    )}
                  </td>
                ))}
                {showActions && (
                  <td className="flex justify-end">
                    <CustomDropdown
                      isRight={true}
                      ContentComponent={() => (
                        <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                          {!noEdit && (
                            <Link
                              to={
                                basePath.replace(
                                  ":agreementPublicId",
                                  params.agreementPublicId
                                ) + `/${item[primaryKey]}`
                              }
                              className="block px-4 py-2 text-sm text-black hover:bg-gray-200 w-full text-left rounded-lg"
                            >
                              <FontAwesomeIcon
                                icon={faPenToSquare}
                                className="mr-4"
                              />
                              Edit
                            </Link>
                          )}
                          {!noDelete && (
                            <button
                              className="block px-4 py-2 text-sm text-red-500 hover:bg-gray-200 w-full text-left rounded-lg"
                              onClick={() =>
                                handleDelete && handleDelete(item[primaryKey])
                              }
                            >
                              <FontAwesomeIcon
                                icon={faCircleMinus}
                                className="mr-4"
                              />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    >
                      <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                        <FontAwesomeIcon icon={faEllipsis} />
                      </div>
                    </CustomDropdown>
                  </td>
                )}
              </tr>
            )})
          ) : (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                <div className="flex flex-col items-center">
                  <FontAwesomeIcon
                    icon={faBoxOpen}
                    className="text-4xl text-gray-300 mt-6 pb-4"
                  />
                  <span>No available information</span>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* sid-kontroll */}
      {data.length >= 10 && (
        <div className="flex gap-4 justify-end items-center text-black pt-4">
          <p className="text-sm">Rows per page</p>
          <div className="w-[72px]">
            <Dropdown
              className="mb-1"
              size="small"
              options={["100", "500", "1000"]}
              onSelect={(rows) => {
                setPage(1); // återgå till första sidan när raderna ändras
                setRowsPerPage(parseInt(rows, 10));
              }}
              selectedOptions={[rowsPerPage.toString()]}
            />
          </div>

          <p className="text-sm">
            {`${startRow}-${endRow} of ${sortedData.length}`}
          </p>
          <FontAwesomeIcon
            className={`cursor-pointer ${page === 1 ? "opacity-50" : ""}`}
            icon={faChevronLeft}
            onClick={() => page > 1 && setPage(page - 1)}
          />
          <FontAwesomeIcon
            className={`cursor-pointer ${
              page === numberOfPages ? "opacity-50" : ""
            }`}
            icon={faChevronRight}
            onClick={() => page < numberOfPages && setPage(page + 1)}
          />
        </div>
      )}
    </div>
  );
};

export default TableView;
